import type { ComponentPropsWithoutRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "core/shadcn/components/ui/tooltip";
import { cn } from "shadcn/lib/utils";

import { gauLabel, gauTooltip } from "./gauLabels";

type GauTermProps = {
  /** When true, renders "GAUs" with the plural tooltip. */
  plural?: boolean;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"span">, "children">;

/**
 * Short-form billing unit label with a dotted underline and hover
 * definition, for use in JSX copy. For non-JSX contexts (CSV headers,
 * aria-labels, emails) use the plain "GAU"/"GAUs" literals — the same
 * strings are also exported as `GAU_SINGULAR`/`GAU_PLURAL` (and `gauLabel()`)
 * from `./gauLabels` if you prefer the constant.
 */
export function GauTerm({ plural = false, className, ...rest }: GauTermProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          {...rest}
          className={cn(
            "cursor-pointer underline decoration-dotted decoration-muted-foreground/70 underline-offset-4",
            className,
          )}
          data-test={plural ? "gau-term-plural" : "gau-term"}
        >
          {gauLabel(plural)}
        </span>
      </TooltipTrigger>
      {/* z-[2100] keeps the definition above the Manage-Plan pack dropdown,
          which portals to the body at zIndex 2000. */}
      <TooltipContent className="z-[2100]">{gauTooltip(plural)}</TooltipContent>
    </Tooltip>
  );
}
